import { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn, fuzzyMatchVi } from '@/lib/utils';

export interface ComboboxOption {
    value: string;
    label: string;
    description?: string;
}

interface SearchableComboboxProps {
    options: ComboboxOption[];
    value?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    className?: string;
    disabled?: boolean;
    clearable?: boolean;
}

/**
 * @deprecated Use ComboboxFK (for entity UUIDs) or ComboboxConfig (for master data) instead.
 * SearchableCombobox — shadcn-styled searchable dropdown
 * Supports typing to filter, scroll-to-select, keyboard nav, clear button.
 */
export function SearchableCombobox({
    options = [],
    value,
    onChange,
    placeholder = 'Chọn...',
    searchPlaceholder = 'Tìm kiếm...',
    emptyText = 'Không tìm thấy',
    className,
    disabled = false,
    clearable = false,
}: SearchableComboboxProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [highlightIndex, setHighlightIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const filtered = useMemo(() => {
        if (!search) return options;
        return options.filter(
            (o) => fuzzyMatchVi(o.label, search) || fuzzyMatchVi(o.description, search)
        );
    }, [options, search]);

    const selectedLabel = (options || []).find((o) => o.value === value)?.label;

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Keyboard nav
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') { setOpen(true); e.preventDefault(); }
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightIndex((i) => Math.max(i - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (filtered[highlightIndex]) {
                    onChange(filtered[highlightIndex].value);
                    setOpen(false);
                    setSearch('');
                }
                break;
            case 'Escape':
                setOpen(false);
                setSearch('');
                break;
        }
    };

    // scroll highlighted into view
    useEffect(() => {
        if (open && listRef.current) {
            const el = listRef.current.children[highlightIndex] as HTMLElement;
            el?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightIndex, open]);

    return (
        <div ref={containerRef} className={cn('relative', className)}>
            <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                    'w-full justify-between h-9 text-sm font-normal',
                    !value && 'text-muted-foreground'
                )}
                onClick={() => { if (!disabled) { setOpen(!open); setTimeout(() => inputRef.current?.focus(), 50); } }}
                disabled={disabled}
                type="button"
            >
                <span className="truncate">{selectedLabel || placeholder}</span>
                <div className="flex items-center gap-1 shrink-0">
                    {clearable && value && (
                        <X
                            className="h-3 w-3 text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); onChange(''); }}
                        />
                    )}
                    <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
            </Button>

            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md animate-in fade-in-0 zoom-in-95">
                    {/* Search input */}
                    <div className="flex items-center border-b px-2">
                        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <input
                            ref={inputRef}
                            className="flex-1 bg-transparent py-2 px-2 text-sm outline-none placeholder:text-muted-foreground"
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setHighlightIndex(0); }}
                            onKeyDown={handleKeyDown}
                        />
                        {search && (
                            <X className="h-3 w-3 text-muted-foreground cursor-pointer" onClick={() => setSearch('')} />
                        )}
                    </div>

                    {/* Options list */}
                    <div ref={listRef} className="max-h-[200px] overflow-y-auto p-1">
                        {filtered.length === 0 ? (
                            <div className="py-4 text-center text-sm text-muted-foreground">{emptyText}</div>
                        ) : (
                            filtered.map((option, i) => (
                                <div
                                    key={option.value}
                                    className={cn(
                                        'flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer',
                                        i === highlightIndex && 'bg-accent',
                                        option.value === value && 'font-medium'
                                    )}
                                    onClick={() => { onChange(option.value); setOpen(false); setSearch(''); }}
                                    onMouseEnter={() => setHighlightIndex(i)}
                                >
                                    <Check className={cn('h-3.5 w-3.5 shrink-0', option.value === value ? 'opacity-100' : 'opacity-0')} />
                                    <div className="min-w-0">
                                        <div className="truncate">{option.label}</div>
                                        {option.description && (
                                            <div className="text-xs text-muted-foreground truncate">{option.description}</div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
